/**
 * A copy opened from a link reads first.
 *
 * The usability study watched a colleague open a sender's link and land in the
 * full editor: the composer with its run chips, "MODEL no key", and the sender's
 * spend line leading the screen, over a graph they had been sent to READ. So a
 * copy opened from a link (`linkCopy`, `SharedCopy.readFirst`) opens on the
 * Overview — the investigation's own reading, the one the sender would have
 * exported — beside the canvas, with this one line at the top of the field in
 * place of the composer: what it is, how much is in it, and two presses. On a
 * phone the Overview is the sheet, raised over the lower half of the field.
 * The store holds the reader's own tab aside while a copy has the pane and
 * hands it back to their own explorations (`readerTab`).
 *
 * Replay how it grew is the first press, because the design report found that
 * watching a graph grow was the moment readers responded to: the transport's
 * own replay, at a copy's bounded pace (`COPY_REPLAY_MS` — the "(N s)" is
 * computed off the same pace the transport's clock reads). It calls no model
 * and spends nothing: it moves the playhead. The camera is left where the reader
 * has it — a replay moves no view — and under reduced motion it is the
 * transport's reduced-motion replay (D191, D224): the claims appear in the order
 * they arrived at the same pace, each strand drawn in the calm third of a second
 * rather than grown, and nothing eases. On a phone the press lowers the sheet
 * first, so the replay has the field and the transport its strip. While a replay
 * is under way the transport owns it — pause, the clock, skip — and the press
 * stays where it is and starts it over, as the palette's row does: a press that
 * stepped aside slid Continue working under the pointer that had just pressed
 * it. The replay ends on the finished graph, and where the Overview is not on
 * screen (a phone's lowered sheet, a folded column) the line's own words become
 * the door back to it.
 *
 * Continue working makes the copy the reader's own and clears the mark for good
 * — the composer comes back, and a reader with no key meets the key prompt the
 * way any first press does. It, and the Overview press, first bring a replay
 * under way back to the finished graph: a copy is never left half-grown behind
 * a door the reader went through. Nothing here spends: the presses move a
 * playhead, a tab and a flag, and Continue working saves.
 *
 * Also continued by working on it. A pass pressed from anywhere else — a node
 * card's verb, a letter key — is the reader taking the copy over, and a composer
 * still hidden would hide that pass's Stop; so the mark goes the moment a pass
 * holds the lock. And it stays gone: an undo can restore a session saved before
 * the press, mark and all, so this tab remembers which copies it continued and
 * clears the mark again if one comes back.
 *
 * Mounted unconditionally in the session column (App.tsx), where Labels, the
 * coach's pins and the run-done column already measure the corner, so the line
 * takes its room without a second measurement.
 */

import { useEffect, useRef } from 'react'
import { useStore } from '../core/store'
import { continuedCopy } from '../core/share'
import { COPY_REPLAY_MS, replaySeconds } from '../core/tempo'
import { STATUS_WORD } from '../core/vocabulary'
import type { Session } from '../core/types'
import { Icon } from './components/Icon'
import { useMedia } from './components/useMedia'

/** Copies this tab has continued. Ids are minted once, so it never needs reset. */
const continued = new Set<string>()

/** Whether the open exploration is a copy still being read first — the one
 *  question every surface that stands down for it asks. */
export const readFirstOf = (s: {
  session: Pick<Session, 'id' | 'shared'>
  busy: boolean
}): boolean => s.session.shared?.readFirst === true && !continued.has(s.session.id) && !s.busy

export function useReadFirst(): boolean {
  return useStore(readFirstOf)
}

/** The fewest arrivals a replay is offered for: the palette's Replay row's own
 *  threshold, so the line and the palette offer it over the same graphs. */
const REPLAY_MIN_NODES = 4

/** A replay under way — playing, or scrubbed to a step — goes to the finished
 *  graph. Every door out of the replay on this line comes through here. */
function toFinishedGraph(): void {
  const s = useStore.getState()
  if (s.playing) s.setPlaying(false)
  if (s.playhead !== null) s.setPlayhead(null)
}

/** Make the open copy the reader's own. Idempotent: a second call finds no mark. */
export function continueWorking(): void {
  toFinishedGraph()
  const s = useStore.getState()
  continued.add(s.session.id)
  const next = continuedCopy(s.session)
  // No step of history and no `rev`: this is not an edit to the exploration, and
  // an undo must not hand the mark back (see the header). Replacing the session
  // object is what schedules the save.
  if (next !== s.session) useStore.setState({ session: next })
}

/**
 * Replay the copy's growth from its seed: the transport's own start — the same
 * three writes as its play button, the palette's row and the space bar — after
 * lowering a phone's sheet, whose strip is where the transport plays. The pace
 * is the transport's to apply (`LedgerDock`, `replayPace`). No model is called.
 */
export function replayCopy(narrow: boolean): void {
  const s = useStore.getState()
  if (narrow && s.railRight) s.setRail(false)
  s.setFocus(null)
  s.setPlayhead(1)
  s.setPlaying(true)
}

/**
 * Back to the Overview from the field: the finished graph, the Overview's tab,
 * and the pane or the phone's sheet raised. Written to the tab directly rather
 * than through `setRightTab`, which is the reader choosing a tab for good — this
 * is the copy's own door, and the tab the store holds aside for the reader's
 * own explorations stays held.
 */
export function readCopyOverview(): void {
  toFinishedGraph()
  useStore.setState({ rightTab: 'breakthroughs' })
  useStore.getState().setRail(true)
}

/** The line's own words, off the record: claims under the seed, and the claims
 *  that carry a source check. Exported for its test. */
export function sharedCopyTally(s: Pick<Session, 'order' | 'nodes'>): string {
  let claims = 0
  let checked = 0
  for (const id of s.order) {
    const n = s.nodes[id]
    if (!n || n.parentId === null) continue
    claims++
    if (n.verification) checked++
  }
  return [
    `${claims} claim${claims === 1 ? '' : 's'}`,
    checked ? `${checked} source check${checked === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

/**
 * D217's rule, for the phone's transport: it stands only while a replay is under
 * way, so the replay's end takes away whatever press of its held focus. Focus
 * goes to what is left in its place — a frame late, after the commit that took
 * the transport away, and only while focus is really lost.
 */
function focusIfLost(target: () => HTMLElement | null): void {
  requestAnimationFrame(() => {
    const here = document.activeElement
    if (here && here !== document.body) return
    target()?.focus({ preventScroll: true })
  })
}

export function SharedCopyBanner() {
  const marked = useStore((s) => s.session.shared?.readFirst === true)
  const id = useStore((s) => s.session.id)
  const busy = useStore((s) => s.busy)
  const session = useStore((s) => s.session)
  const tempo = useStore((s) => s.tempo)
  // A replay under way: playing, or stood at a step.
  const replaying = useStore((s) => s.playhead !== null)
  // Where the Overview could be on screen: the column, or the phone's sheet.
  const paneUp = useStore((s) => s.railRight)
  const narrow = useMedia('(max-width: 767px)')
  const gone = continued.has(id)
  const replayRef = useRef<HTMLButtonElement | null>(null)
  const overviewRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (marked && (busy || gone)) continueWorking()
  }, [marked, busy, gone])

  // The replay's end hands focus back to the line when the transport that held
  // it left with the replay. The Overview's door first where it is offered: it
  // is the door the replay ends beside.
  const wasReplaying = useRef(replaying)
  useEffect(() => {
    const ended = wasReplaying.current && !replaying
    wasReplaying.current = replaying
    if (ended) focusIfLost(() => overviewRef.current ?? replayRef.current)
  }, [replaying])

  if (!marked || busy || gone) return null
  const offerReplay = session.order.length >= REPLAY_MIN_NODES
  const seconds = replaySeconds(session.order.length, tempo, COPY_REPLAY_MS)
  const replayLabel = `Replay how it grew (${seconds} s)`
  const tally = sharedCopyTally(session)
  const what = (
    <>
      <span className="sharedline__mark">{STATUS_WORD.sharedCopy}</span>
      <span className="num">{tally}</span>
    </>
  )
  return (
    <div className="sharedline panel" role="region" aria-label={STATUS_WORD.sharedCopy}>
      {/* What the copy is — and, where the Overview is not on screen (a phone's
          lowered sheet, a folded column), the door back to it. The counts are
          the Overview's own, and the chevron is the coach's line's idiom, one
          row below: a third press would take the phone's line to three rows. */}
      {paneUp ? (
        <span className="sharedline__what">{what}</span>
      ) : (
        <button
          ref={overviewRef}
          className="sharedline__what sharedline__open"
          aria-label={`${STATUS_WORD.sharedCopy} · ${tally} — open the Overview`}
          title="Open the Overview"
          onClick={readCopyOverview}
        >
          {what}
          <Icon name="chevron" size={11} />
        </button>
      )}
      <span className="sharedline__presses">
        {offerReplay && (
          <button
            ref={replayRef}
            className="btn btn--outline sharedline__go"
            aria-label={`${replayLabel} · ${STATUS_WORD.free.toLowerCase()}`}
            title={`Each claim in the order it arrived, in about ${seconds} seconds`}
            onClick={() => replayCopy(narrow)}
          >
            <Icon name="play" size={11} />
            {replayLabel}
          </button>
        )}
        <button
          className="btn btn--outline sharedline__go"
          aria-label="Continue working on this copy as your own"
          onClick={continueWorking}
        >
          Continue working
        </button>
      </span>
    </div>
  )
}
